import { OrganizationPublicId } from "../../../common/service/server/Types";
import React, { useRef, useState } from "react";
import { Api } from "../../../common/service/Api";
import { Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import { Field, Form } from "react-final-form";
import { CloseableDialogTitle } from "./CloseableDialogTitle";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { useActionRequest } from "../../../common/hooks/api";

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

export const PDF_FILE_TYPES = ["application/pdf"];

interface DPAPdfUploadDialogProps {
  organizationId: OrganizationPublicId;
  dpaId: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  uploadPdf?: (file: File) => Promise<unknown>;
  title?: string;
  subtitle?: string;
  uploadButtonText?: string;
}

export const DPAPdfUploadDialog = ({
  organizationId,
  dpaId,
  open,
  onClose,
  onDone,
  uploadPdf,
  title = "Upload PDF",
  subtitle = "File type needs to be .PDF only",
  uploadButtonText = "Upload",
}: DPAPdfUploadDialogProps) => {
  const apiFn = (pdfFile: File) =>
    uploadPdf
      ? uploadPdf(pdfFile)
      : Api.dataProtectionAssessment.uploadUnsignedPdf(organizationId, dpaId, pdfFile);

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);

  const { fetch: postPdf, request: postPdfRequest } = useActionRequest({
    api: () => apiFn(pdfFile),
    messages: {
      forceError: "Unable to upload PDF, please try again later",
      success: "PDF successfully uploaded.",
    },
    onSuccess: () => {
      onDone();
      setPdfFile(null);
      onClose();
    },
    notReady: pdfFile == null,
  });

  const handleFileChange = (onChange) => (event) => {
    if (event.target?.files?.length > 0) {
      const file = event.target?.files[0];
      setPdfFile(file);
    }
    onChange(event);
  };

  const removePdf = () => {
    setPdfFile(null);
  };

  return (
    <Dialog
      className="logo-upload-dialog"
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={() => {
        setPdfFile(null);
        onClose();
      }}
    >
      <Form
        onSubmit={postPdf}
        validate={() => {
          const errors = {};
          if (pdfFile && pdfFile.size > MAX_FILE_SIZE_BYTES) {
            errors["pdfFile"] = "File must be less than 20MB in size";
          }

          if (pdfFile && !PDF_FILE_TYPES.includes(pdfFile.type)) {
            errors["pdfFile"] = "File must be a PDF";
          }

          return errors;
        }}
        render={({ handleSubmit, errors }) => {
          return (
            <form onSubmit={handleSubmit}>
              <CloseableDialogTitle
                onClose={() => {
                  setPdfFile(null);
                  onClose();
                }}
                subtitle={
                  <Typography variant="subtitle2" component="span" style={{ display: "block" }}>
                    {subtitle}
                  </Typography>
                }
              >
                {title}
              </CloseableDialogTitle>

              <DialogContent>
                <div className="logo-upload-dialog--content">
                  <div
                    className={`logo-image-preview ${
                      dragOver ? "logo-upload-dialog--dragover" : ""
                    }`}
                    id="logo-image-preview"
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      setDragOver(false);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      let file;
                      if (event.dataTransfer.items) {
                        // Use DataTransferItemList interface to access the file(s)
                        [...Array.from(event.dataTransfer.items)].forEach((item) => {
                          // If dropped items aren't files, reject them
                          if (!file && item.kind === "file") {
                            file = item.getAsFile();
                          }
                        });
                      } else {
                        // Use DataTransfer interface to access the file(s)
                        [...Array.from(event.dataTransfer.files)].forEach((f) => {
                          if (!file) {
                            file = f;
                          }
                        });
                      }
                      if (file) {
                        const container = new DataTransfer();
                        container.items.add(file);
                        pdfInputRef.current.files = container.files;
                        setPdfFile(file);
                        pdfInputRef.current.dispatchEvent(new Event("change"));
                      }
                      setDragOver(false);
                    }}
                  >
                    {pdfFile && <div className="logo-image-preview--image">{pdfFile.name}</div>}
                    {!pdfFile && (
                      <>
                        <div className="logo-image-preview--droptext">
                          Drag and drop your PDF file here
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-lg w-100">
                    <Field name="pdfFile">
                      {({ input: { onChange, ...inputProps }, meta: { error } }) => {
                        const handleChange = handleFileChange(onChange);
                        return (
                          <>
                            <div className="flex-row flex--justify-center">
                              <input
                                hidden={true}
                                id="logo-image-file-input"
                                ref={pdfInputRef}
                                {...inputProps}
                                onChange={handleChange}
                                type="file"
                                accept={PDF_FILE_TYPES.join(",")}
                              />
                              <label htmlFor="logo-image-file-input">
                                <span className="logo-upload-dialog--input-remove">
                                  <Button
                                    className="w-192 mr-md"
                                    onClick={removePdf}
                                    disabled={!pdfFile}
                                    size="large"
                                  >
                                    Remove
                                  </Button>
                                </span>
                                <span className="logo-upload-dialog--input-add">
                                  <Button
                                    className="w-192"
                                    variant="contained"
                                    onClick={() => pdfInputRef.current?.click()}
                                    size="large"
                                  >
                                    Choose PDF
                                  </Button>
                                </span>
                              </label>
                            </div>
                            {error && (
                              <div className="mt-md text-center text-error text-t2 ml-md">
                                {error}
                              </div>
                            )}
                          </>
                        );
                      }}
                    </Field>
                  </div>
                </div>
              </DialogContent>

              <DialogActions>
                <LoadingButton
                  className="w-192"
                  type="submit"
                  color="primary"
                  loading={postPdfRequest.running}
                  disabled={!pdfFile || errors?.pdfFile}
                >
                  {uploadButtonText}
                </LoadingButton>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};
